'use client';

import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import Image from 'next/image';
import Link from 'next/link';
import { formatVariantSelection } from '@/lib/productUtils';
import { imageLoader } from '@/lib/imageLoader';

export default function Cart() {
  const {
    cart,
    isCartOpen,
    toggleCart,
    removeFromCart,
    updateQuantity,
    getCartTotal,
    clearCart
  } = useCart();
  const { isLoggedIn } = useAuth();

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={toggleCart}
      ></div>

      {/* Cart panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold">Your Cart</h2>
            <button
              onClick={toggleCart}
              className="p-2 rounded-full hover:bg-gray-100"
              aria-label="Close cart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Your cart is empty</p>
                <Link
                  href="/products"
                  className="btn btn-primary"
                  onClick={toggleCart}
                >
                  Continue Shopping
                </Link>
              </div>
            ) : (
              <ul className="space-y-4">
                {cart.map((item) => (
                  <li key={item.cartItemId} className="flex border-b pb-4">
                    <div className="relative h-20 w-20 flex-shrink-0 rounded-md overflow-hidden bg-gray-100">
                      {item.images && item.images.length > 0 ? (
                        <Image
                          src={item.images[0]}
                          alt={item.name}
                          fill
                          sizes="80px"
                          className="object-cover"
                          loader={imageLoader}
                          unoptimized={false}
                          onError={(e) => {
                            console.error('Cart image failed to load:', e);
                          }}
                        />
                      ) : item.imageUrl ? (
                        <Image
                          src={item.imageUrl}
                          alt={item.name}
                          fill
                          sizes="80px"
                          className="object-cover"
                          loader={imageLoader}
                          unoptimized={false}
                          onError={(e) => {
                            console.error('Cart image URL failed to load:', e);
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                          No Image
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex-1">
                      <div className="flex justify-between">
                        <h3 className="text-sm font-medium">{item.name}</h3>
                        <button
                          onClick={() => removeFromCart(item.cartItemId)}
                          className="text-gray-400 hover:text-red-500"
                          aria-label="Remove item"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {/* Display selected variants if any */}
                      {item.selectedVariants && Object.keys(item.selectedVariants).length > 0 && (
                        <p className="text-xs text-gray-600 mb-1">
                          {formatVariantSelection(item.selectedVariants)}
                        </p>
                      )}

                      <p className="text-sm mb-1">
                        {item.salePrice &&
                         item.saleStartDate &&
                         item.saleEndDate &&
                         new Date() >= new Date(item.saleStartDate) &&
                         new Date() <= new Date(item.saleEndDate) ? (
                          <>
                            <span className="text-gray-500 line-through mr-2">${item.price.toFixed(2)}</span>
                            <span className="text-red-600">${item.salePrice.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="text-gray-500">${item.price.toFixed(2)}</span>
                        )}
                      </p>

                      <div className="flex items-center">
                        <button
                          onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                          className="text-gray-500 hover:text-gray-700 border rounded-md w-6 h-6 flex items-center justify-center"
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <span className="mx-2 text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                          className="text-gray-500 hover:text-gray-700 border rounded-md w-6 h-6 flex items-center justify-center"
                        >
                          +
                        </button>

                        <span className="ml-auto font-medium">
                          ${((item.salePrice &&
                              item.saleStartDate &&
                              item.saleEndDate &&
                              new Date() >= new Date(item.saleStartDate) &&
                              new Date() <= new Date(item.saleEndDate)
                                ? item.salePrice
                                : item.price) * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {cart.length > 0 && (
            <div className="border-t p-4 space-y-4">
              <div className="flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span>${getCartTotal().toFixed(2)}</span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Link
                  href="/products"
                  className="btn border border-gray-300 hover:bg-gray-100"
                  onClick={toggleCart}
                >
                  Continue Shopping
                </Link>

                <button
                  onClick={clearCart}
                  className="btn border border-gray-300 hover:bg-gray-100"
                >
                  Clear Cart
                </button>

                <Link
                  href={isLoggedIn ? "/checkout" : "/login?redirect=/checkout"}
                  className="btn btn-primary"
                  onClick={toggleCart}
                >
                  Checkout
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
